import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { QueryFilterOperators } from 'types/api';
import { UnifiedQueryParams } from 'types/unified/api';
import { baseQueryWithReauth } from 'store/slices/api';
import { RootState } from 'store';
import { processOverrideOrgIdParams } from 'store/slices/utils/index';

const warehouseStorageName = 'warehouse';

/**
 * Creates a query function for Unified API endpoints.
 *
 * Identical behavior to createClientV2QueryFn but uses UnifiedQueryParams.
 * Automatically injects org filter (unless in blended mode) and handles
 * warehouse sandbox filtering.
 *
 * @param urlFn - Function that generates the URL from params
 * @returns RTK Query base query function
 *
 * @example
 * createUnifiedQueryFn<ListResponse<BomSchema>, UnifiedQueryParams<BomSchema>>(
 *   (params) => ({
 *     url: UNIFIED_URLS.BOMS(parseUnifiedQueryParams(params)),
 *   })
 * )
 */
export const createUnifiedQueryFn =
  <T, P extends UnifiedQueryParams<any>>(
    urlFn: (params: P) => string | FetchArgs
  ): BaseQueryFn<P & { overrideOrgId?: string }, T, FetchBaseQueryError> =>
  async (params, api) => {
    try {
      const activeOrgId = (api.getState() as RootState).org.activeOrgId;
      const blended = (api.getState() as RootState).org.blended;

      // Extract overrideOrgId from params and remove it for urlFn
      const { orgId, paramsWithoutOverride } = processOverrideOrgIdParams(
        params,
        activeOrgId
      );

      const storedValue = localStorage.getItem(warehouseStorageName);
      const sandbox =
        storedValue === null ? false : JSON.parse(storedValue).sandboxMode;

      const paramsWithOrg = {
        ...paramsWithoutOverride,
        filters: [
          ...(paramsWithoutOverride.filters || []),
          ...(blended || paramsWithoutOverride.blended
            ? []
            : [
                {
                  field: 'org',
                  operator: QueryFilterOperators['='],
                  value: orgId,
                },
              ]),
          // This section basically means if the user in warehouse lets look at the sandbox setting used in local storage used for warehouse
          ...(blended
            ? [
                {
                  field: 'org.sandbox',
                  operator: QueryFilterOperators['='],
                  value: sandbox,
                },
              ]
            : []),
        ] as P['filters'],
      } as P;

      const baseArgs = urlFn(paramsWithOrg);
      const fetchArgs =
        typeof baseArgs === 'string' ? { url: baseArgs } : baseArgs;

      const result = await baseQueryWithReauth(fetchArgs, api, {});

      if (result.error) {
        return { error: result.error };
      }

      return { data: result.data as T };
    } catch (error) {
      return {
        error: {
          status: 'CUSTOM_ERROR',
          error: error instanceof Error ? error.message : String(error),
        } as FetchBaseQueryError,
      };
    }
  };
